import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import * as ReactDOMServer from 'react-dom/server';
import {
    CCol,
    CFormInput,
    CFormLabel,
    CInputGroup,
    CInputGroupText,
} from '@coreui/react-pro';
import fetchRequest from 'src/network';
import { getBookingStatusString, getTitleKey, isNotNull, removeBracketId, toFormattedSingleDate, toFormattedSingleTime } from './constants';
import moment from 'moment';
import './common/css/sweetPopup.css';
import { QRCodeSVG } from 'qrcode.react';
import "./commonStyle.css";
import { FaRegEye } from 'react-icons/fa6';
import { RiLockPasswordLine } from 'react-icons/ri';
import styles from './common/styles/sweetPopup.styles';


const swalDelete = (deleteFunction, props) => {
    Swal.fire({
        title: "Are you sure you want to permanently delete this item?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
    }).then(async (result) => {
        if (result.isConfirmed) {
            let res = await deleteFunction(props);
            if (!res) return;
            else if (res?.status_code === 200) {
                Swal.fire({
                    title: "Deleted!",
                    text: res?.message ?? "Entry has been deleted!",
                    icon: "success"
                });
            } else {
                Swal.fire({
                    title: "Error!",
                    text: res?.message ?? "Something went wrong!",
                    icon: "error"
                });
            }
        }
    })
};

const swalConfirm = (message, updateFunction, props) => {
    Swal.fire({
        title: message,

        html: ReactDOMServer.renderToStaticMarkup(
            <div className="p-3">
                <CCol>
                    <p>Updating contract rates after bookings and invoices exist may cause billing discrepancies, invoice mismatches, reporting inconsistencies, or reconciliation issues.</p>
                    <p>This change will affect bookings that are already linked to the contract. Existing invoices will not be automatically corrected and may no longer match the updated contract rates.</p>
                    <p>Only proceed if you fully understand the impact and have verified that invoice and billing adjustments will be handled manually.</p>
                </CCol>
            </div>
        ),
        text: "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "I Understand the Risk — Force Update",
        cancelButtonText: "Cancel",
    }).then(async (result) => {
        if (result.isConfirmed) {
            let res = await updateFunction(props);
            if (!res) return;
            else if (res?.status_code === 200) {
                Swal.fire({
                    title: "Updated!",
                    text: res?.message ?? "Entry has been updated!",
                    icon: "success"
                });
            } else {
                Swal.fire({
                    title: "Error!",
                    text: res?.message ?? "Something went wrong!",
                    icon: "error"
                });
            }
        }
    })
};

const swalCancel = (cancelFunction, props) => {
    Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, cancel it!",
        customClass: {
            confirmButton: "btn btn-md text-white",
            cancelButton: "btn btn-md text-white"
        },
    }).then((result) => {
        if (result.isConfirmed) {
            let res = cancelFunction(props);
            if (res) {
                Swal.fire({
                    title: "Cancelled!",
                    text: "Entry has been cancelled!",
                    icon: "success"
                });
            }
        }
    })
};

const swalCancelWithReason = (cancelFunction, props) => {
    Swal.fire({
        title: "Are you sure?",
        text: "Enter reason for booking cancellation!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, cancel it!",
        input: "text",
        showLoaderOnConfirm: true,
        customClass: {
            confirmButton: "btn btn-md text-white",
            cancelButton: "btn btn-md text-white"
        },
        preConfirm: async (data) => {
            if (!isNotNull(data)) {
                Swal.showValidationMessage("Cancel reason is required!");
            }
        }
    }).then((result) => {
        if (result.isConfirmed) {
            if (isNotNull(result?.value)) {
                let res = cancelFunction(props, result.value);
                if (res) {
                    Swal.fire({
                        title: "Cancelled!",
                        text: "Entry has been cancelled!",
                        icon: "success"
                    });
                }
            }
        }
    })
};

const swalDeleteWithReason = (deleteFunction, props) => {
    Swal.fire({
        title: "Are you sure?",
        text: "Enter reason for booking deletion!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
        input: "text",
        showLoaderOnConfirm: true,
        customClass: {
            confirmButton: "btn btn-md text-white",
            cancelButton: "btn btn-md text-white"
        },
        preConfirm: async (data) => {
            if (!isNotNull(data)) {
                Swal.showValidationMessage("Cancel reason is required!");
            }
        }
    }).then((result) => {
        if (result.isConfirmed) {
            if (isNotNull(result?.value)) {
                let res = deleteFunction(props, result.value);
                if (res) {
                    Swal.fire({
                        title: "Deleted!",
                        text: "Entry has been deleted!",
                        icon: "success"
                    });
                }
            }
        }
    })
};

const swalError = (errMsg) => {
    Swal.fire({
        icon: "error",
        title: errMsg?.message ?? "Error!",
        text: errMsg?.error_message ?? "Something went wrong!",
        confirmButtonText: 'Okay',
        customClass: {
            confirmButton: "btn btn-md text-white bg-danger"
        }
    });
}

const swalWarning = (message) => {
    Swal.fire({
        icon: "warning",
        title: message?.title ?? "Warning!",
        text: message?.message ?? "Something went wrong!",
        confirmButtonText: 'Okay',
        customClass: {
            confirmButton: "btn btn-md text-white bg-warning"
        }
    });
}

const swalInfo = (message) => {
    Swal.fire({
        icon: "info",
        title: message?.title ?? "Information",
        html: message?.message ?? "",
        confirmButtonText: 'Okay',
        customClass: {
            confirmButton: "btn btn-sm btn-info text-white"
        }
    });
}

const swalSuccess = (message, timer = 3000) => {
    let timerInterval;
    Swal.fire({
        icon: "success",
        title: "Success!",
        text: message ?? "Success!",
        timer: timer,
        showConfirmButton: false,
        willClose: () => {
            clearInterval(timerInterval);
        },
        customClass: {
            confirmButton: "btn btn-md text-white bg-success"
        }
    });
};

function showTransparentBackground() {
    const existing = document.getElementById("kuruma-backdrop");
    if (existing) return;

    const gifOverlay = document.createElement('div');
    gifOverlay.id = 'kuruma-backdrop';
    gifOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: url('https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExODFlMDdsZ3I0eDA5MGgxZzJpd2h1MzJodG5nZnAxcHN4amZrdnk0cyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/MOXTa4jE5nwhOtILCT/giphy.gif') center center no-repeat;
    background-size: cover;
    pointer-events: none;
    z-index: 1050;
    opacity: 0.3;
  `;
    document.body.appendChild(gifOverlay);
}



const MySwal = withReactContent(Swal);

const swalFirstBookingSuccess = () => {
    showTransparentBackground(); // ✅ Add transparent GIF overlay

    MySwal.fire({
        title: '', // No default title
        html: `
            <div>
                <div class="header-row">
                    <div class="icon-circle">
                        <span>🎉</span>
                    </div>
                    <div class="header-text">
                        <div class="badge">Priority Alert</div>
                        <h2>First Booking Alert!</h2>
                    </div>
                </div>
                <div class="content">
                    <p>We've just received the <strong>first booking</strong> from this customer.</p>
                    <p>Please ensure they have an <strong>awesome experience</strong> — right from communication to service delivery.</p>
                    <p><strong>Let's make this a lasting impression!</strong> 💪✨</p>
                </div>
            </div>
        `,
        icon: 'none',
        showConfirmButton: true,
        confirmButtonText: 'View Details',
        customClass: {
            popup: 'modal-compact',
            confirmButton: 'btn-primary'
        },
        willClose: () => {
            const el = document.getElementById("kuruma-backdrop");
            if (el) el.remove();
        },
    });
};


const swalSetPassword = async ({
    title = "Set Password",
    userId = 0,
}) => {

    await Swal.fire({
        title: title,
        html: ReactDOMServer.renderToStaticMarkup(
            <div className="p-3">

                {/* Password */}
                <CCol>
                    <CFormLabel className="text-start w-100">
                        Password <span className="text-danger ms-2">*</span>
                    </CFormLabel>
                    <CInputGroup className="mb-4 rounded-3">
                        <CInputGroupText>
                            <RiLockPasswordLine size={20} />
                        </CInputGroupText>
                        <CFormInput
                            type="password"
                            id="password1"
                            placeholder="Enter password"
                            className="border border-secondary pe-5"
                        />
                        <CInputGroupText
                            id="togglePass1"
                            style={{ cursor: "pointer" }}
                        >
                            <FaRegEye size={20} />
                        </CInputGroupText>
                    </CInputGroup>
                </CCol>

                {/* Confirm Password */}
                <CCol className="mt-3">
                    <CFormLabel className="text-start w-100">
                        Confirm Password <span className="text-danger ms-2">*</span>
                    </CFormLabel>
                    <CInputGroup className="mb-4 rounded-3">
                        <CInputGroupText>
                            <RiLockPasswordLine size={20} />
                        </CInputGroupText>
                        <CFormInput
                            type="password"
                            id="password2"
                            placeholder="Enter confirm password"
                            className="border border-secondary pe-5"
                        />
                        <CInputGroupText
                            id="togglePass2"
                            style={{ cursor: "pointer" }}
                        >
                            <FaRegEye size={20} />
                        </CInputGroupText>
                    </CInputGroup>
                </CCol>
            </div>
        ),

        focusConfirm: false,
        confirmButtonText: "Save",
        showCancelButton: true,
        cancelButtonText: "Cancel",

        customClass: {
            confirmButton: "btn btn-md text-white bg-success",
            cancelButton: "btn btn-md text-white bg-warning",
        },

        showLoaderOnConfirm: true,

        /** 🔥 Add event listeners after popup opens */
        didOpen: () => {
            const toggle = (inputId, toggleId) => {
                const input = document.getElementById(inputId);
                const toggleBtn = document.getElementById(toggleId);

                toggleBtn.addEventListener("click", () => {
                    const isPassword = input.type === "password";

                    input.type = isPassword ? "text" : "password";

                    toggleBtn.innerHTML = isPassword
                        ? '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 640 512" height="20" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zm151 118.3C226 97.7 269.5 80 320 80c65.2 0 118.8 29.6 159.9 67.7C518.4 183.5 545 226 558.6 256c-12.6 28-36.6 66.8-70.9 100.9l-53.8-42.2c9.1-17.6 14.2-37.5 14.2-58.7c0-70.7-57.3-128-128-128c-32.2 0-61.7 11.9-84.2 31.5l-46.1-36.1zM394.9 284.2l-81.5-63.9c4.2-8.5 6.6-18.2 6.6-28.3c0-5.5-.7-10.9-2-16c.7 0 1.3 0 2 0c44.2 0 80 35.8 80 80c0 9.9-1.8 19.4-5.1 28.2zm9.4 130.3C378.8 425.4 350.7 432 320 432c-65.2 0-118.8-29.6-159.9-67.7C121.6 328.5 95 286 81.4 256c8.3-18.4 21.5-41.5 39.4-64.8L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5l-41.9-33zM192 256c0 70.7 57.3 128 128 128c13.3 0 26.1-2 38.2-5.8L302 334c-23.5-5.4-43.1-21.2-53.7-42.3l-56.1-44.2c-.2 2.8-.3 5.6-.3 8.5z"></path></svg>'
                        : '<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 576 512" height="20" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M288 80c-65.2 0-118.8 29.6-159.9 67.7C89.6 183.5 63 226 49.4 256c13.6 30 40.2 72.5 78.6 108.3C169.2 402.4 222.8 432 288 432s118.8-29.6 159.9-67.7C486.4 328.5 513 286 526.6 256c-13.6-30-40.2-72.5-78.6-108.3C406.8 109.6 353.2 80 288 80zM95.4 112.6C142.5 68.8 207.2 32 288 32s145.5 36.8 192.6 80.6c46.8 43.5 78.1 95.4 93 131.1c3.3 7.9 3.3 16.7 0 24.6c-14.9 35.7-46.2 87.7-93 131.1C433.5 443.2 368.8 480 288 480s-145.5-36.8-192.6-80.6C48.6 356 17.3 304 2.5 268.3c-3.3-7.9-3.3-16.7 0-24.6C17.3 208 48.6 156 95.4 112.6zM288 336c44.2 0 80-35.8 80-80s-35.8-80-80-80c-.7 0-1.3 0-2 0c1.3 5.1 2 10.5 2 16c0 35.3-28.7 64-64 64c-5.5 0-10.9-.7-16-2c0 .7 0 1.3 0 2c0 44.2 35.8 80 80 80zm0-208a128 128 0 1 1 0 256 128 128 0 1 1 0-256z"></path></svg>';
                });
            };

            toggle("password1", "togglePass1");
            toggle("password2", "togglePass2");
        },

        preConfirm: async () => {
            const password = document.getElementById("password1")?.value;
            const confirmPassword = document.getElementById("password2")?.value;

            if (!password || !confirmPassword) {
                Swal.showValidationMessage("Fields must not be blank!");
                return false;
            }

            if (password !== confirmPassword) {
                Swal.showValidationMessage("Passwords do not match!");
                return false;
            }

            return fetchRequest
                ?.post(`/auth/set-password/${userId}`, {
                    password,
                    confirm_password: confirmPassword,
                })
                .then((response) => {
                    if (response?.status_code === 200) {
                        swalSuccess(response?.message);
                    } else {
                        swalError(response);
                    }
                })
                .catch((error) => {
                    swalError(error);
                });
        },
    });
};


const swalStatusUpdate = (id, status = "", statusSetter, setIsRefresh, isRefresh) => {
    Swal.fire({
        title: "Status Remarks",
        text: "",
        input: "text",
        inputAttributes: {
            autocapitalize: "off"
        },
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Update Status",
        showLoaderOnConfirm: true,
        customClass: {
            confirmButton: "btn btn-md text-white",
            cancelButton: "btn btn-md text-white"
        },
        preConfirm: async (data) => {
            if (isNotNull(data)) {
                try {
                    let body = {
                        status: status,
                        status_remarks: data
                    }
                    fetchRequest.post(`/booking/${id}/status-update`, body)
                        .then((response) => {
                            if (response?.status_code === 200) {
                                statusSetter(status);
                                swalSuccess(response?.message);
                                setIsRefresh(!isRefresh);
                            } else {
                                swalError(response);
                            }
                        })
                        .catch((error) => {
                            swalError(error);
                        });
                } catch (error) {
                    swalError(error);
                }
            } else {
                Swal.showValidationMessage("A reason for this action is required!");
            }
        },
        allowOutsideClick: () => !Swal.isLoading()
    });
}

const swalColorCodeInfo = (data = []) => {
    Swal.fire({
        title: "Color Code Info",
        icon: "none",
        html: ReactDOMServer.renderToStaticMarkup(
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <table className='modal-table' style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr className='bg-secondary text-light fix-pos modal-tr'>
                            <th className='modal-th'>OPTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data?.map((i, idx) => (
                                <tr key={idx} className='modal-tr'>
                                    <td className='modal-td' style={{ background: i?.color }}>
                                        <small><i>{i?.name}</i></small>
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        ),
        showCloseButton: true,
        showConfirmButton: false,
        confirmButtonText: 'Close',
        customClass: {
            confirmButton: "btn btn-md text-white bg-danger",
            popup: "w-h-50",
        },
        showClass: {
            popup: "animate__animated animate__fadeInDown"
        },
        hideClass: {
            popup: "animate__animated animate__fadeOutUp"
        }
    });
};

const swalStatusLogs = (data = "", statusColor) => {
    Swal.fire({
        title: "Status Logs",
        icon: "none",
        html: ReactDOMServer.renderToStaticMarkup(
            <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                <table className='modal-table' style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr className='bg-secondary text-light fix-pos modal-tr'>
                            <th className='modal-th'>Date & Time</th>
                            <th className='modal-th'>Status</th>
                            <th className='modal-th'>Status Remarks</th>
                            <th className='modal-th'>Update Type</th>
                            <th className='modal-th'>User</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data?.map((i, idx) => (
                                <tr key={idx} className='modal-tr'>
                                    <td className='modal-td'><small>{moment(i?.datetime).format('ll HH:mm')}</small></td>
                                    <td className='modal-td'>
                                        <code>
                                            <small style={{ color: statusColor(i?.status) }}>
                                                <b>{getBookingStatusString(i?.status)}</b>
                                            </small>
                                        </code>
                                    </td>
                                    <td className='modal-td'><small>{i?.status_remarks}</small></td>
                                    <td className='modal-td'><small>{i?.update_type}</small></td>
                                    <td className='modal-td'><small>{removeBracketId(i?.user_details)}</small></td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        ),
        showCloseButton: true,
        showConfirmButton: false,
        confirmButtonText: 'Close',
        customClass: {
            confirmButton: "btn btn-md text-white bg-danger",
            popup: "w-75",
        },
        showClass: {
            popup: "animate__animated animate__fadeInDown"
        },
        hideClass: {
            popup: "animate__animated animate__fadeOutUp"
        }
    });
};



const swalQRCode = (title = "QR Code", data = "") => {
    Swal.fire({
        icon: "none",
        title: title,
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Download",
        html: ReactDOMServer.renderToStaticMarkup(
            <div className='text-center'>
                <span ><QRCodeSVG value={data} size={256} id={"svg-qr"} /></span>
            </div>
        ),
        customClass: {
            confirmButton: "btn btn-md text-white",
            cancelButton: "btn btn-md text-white"
        },
        preConfirm: () => {
            var svg = document.getElementById("svg-qr");

            //get svg source.
            var serializer = new XMLSerializer();
            var source = serializer.serializeToString(svg);
            //convert svg source to URI data scheme.
            var image = new Image();
            image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(source);

            image.onload = function () {
                var canvas = document.createElement('canvas');
                canvas.width = image.width;
                canvas.height = image.height;
                var context = canvas.getContext('2d');
                context.drawImage(image, 0, 0);

                //create a png image from the canvas
                var pngFile = canvas.toDataURL('image/png');

                //create a download link and click it
                var downloadLink = document.createElement('a');
                downloadLink.href = pngFile;
                downloadLink.download = 'qrcode.png';
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            };
        }
    });
};

const swalPersistent = (message, callBack) => {
    Swal.fire({
        icon: "success",
        title: "Success!",
        text: message ?? "Success!",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: true,
        confirmButtonText: "Okay",
        customClass: {
            confirmButton: "btn btn-md text-white bg-success"
        },
        willClose: () => {
            if (typeof callBack === "function") {
                callBack();
            }
        }
    });
};

const swalBookingSummaryDetails = (item) => {
    let summaryData = [
        ["Booking Ref No.", item?.booking_number ?? "-"],
        ["Guest Name", `${getTitleKey(item?.customer_user_title)} ${item?.customer_user_name}`],
        ["Guest Mobile No.", item?.customer_user_mobile],
        ["Booked by", `${getTitleKey(item?.customer_contact_title)} ${item?.customer_contact_name}`],
        ["Report From Date", toFormattedSingleDate(item?.from_date)],
        ["Report Time", toFormattedSingleTime(item?.reporting_time)],
        ["Service Location", item?.service_location?.name],
        ["Reporting Address", item?.pick_from_detail],
        ["No. of Vehicles", item?.number_of_vehicle],
        ["Vehicle Type", `${item?.vehicle_model?.make} ${item?.vehicle_model?.name}`],
        ["Vehicle Number", item?.vehicle_reg_no ?? item?.vehicle?.reg_no ?? "-"],
        // Add only if dispatched_at is not null
        ...(isNotNull(item?.dispatched_at)
            ? [
                [
                    "Driver Name",
                    (item?.driver_name ||
                        `${getTitleKey(item?.driver?.details?.title)} ${item?.driver?.details?.full_name}`) ?? "-"
                ],
            ]
            : []),
        ...(isNotNull(item?.dispatched_at)
            ? [
                [
                    "Driver Mobile No.",
                    (item?.driver_mobile ||
                        item?.driver?.details?.mobile) ?? "-"
                ],
            ]
            : []),
        ["Travel Type", item?.duty_type ?? "-"],
        ["Outstation Name", item?.outstation_details ?? "-"],
        ["Special Instructions", item?.special_instruction ?? "-"],
    ]
    Swal.fire({
        title: "",
        icon: "none",
        html: ReactDOMServer.renderToStaticMarkup(
            <div style={styles?.summaryContainer}>
                <table style={styles?.summaryTable}>
                    <thead>
                        <tr>
                            <th
                                colSpan="2"
                                style={styles?.summaryHeader}
                            >
                                Booking Details
                            </th>
                        </tr>
                    </thead>

                    <tbody style={styles?.summaryBody}>
                        {summaryData && summaryData.map(([label, value], index) => (
                            <tr key={index}>
                                <td style={styles?.summaryRowHead}>{label}</td>
                                <td style={styles?.summaryRowContent}>{value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ),
        showCloseButton: true,
        confirmButtonText: "",
        showConfirmButton: false,
        customClass: {
            popup: "booking-summary-popup",
            confirmButton: "btn btn-md text-white bg-danger"
        },
        showClass: {
            popup: "animate__animated animate__fadeInDown"
        },
        hideClass: {
            popup: "animate__animated animate__fadeOutUp"
        }
    });
};





export {
    swalDelete,
    swalError,
    swalWarning,
    swalSuccess,
    swalInfo,
    swalFirstBookingSuccess,
    swalSetPassword,
    swalCancel,
    swalCancelWithReason,
    swalDeleteWithReason,
    swalStatusUpdate,
    swalStatusLogs,
    swalQRCode,
    swalPersistent,
    swalColorCodeInfo,
    swalBookingSummaryDetails,
    swalConfirm
};